import valid from "card-validator";
import { business_integrations } from "lobbie";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { FieldErrors, Path, UseFormRegister } from "react-hook-form";
import SuspendedAddressAutocomplete from "src/components/shared/elements/AddressAutocomplete";
import LobbieSpinner from "src/components/shared/loading/LobbieSpinner";

// Base interface that any form schema must include for credit card fields
interface CreditCardFormFields {
    cardholderName: string;
    cardNumber: string;
    expiryMonth: string;
    expiryYear: string;
    cvv: string;
    billingAddress?: string;
}

interface CreditCardInputProps<TFormData extends CreditCardFormFields = CreditCardFormFields> {
    onCardDetailsChange: (cardDetails: business_integrations.ICardDetails) => void;
    initialCardDetails?: Partial<business_integrations.ICardDetails>;
    isReadOnly?: boolean;
    hideBillingAddress?: boolean;
    register?: UseFormRegister<TFormData>;
    errors?: FieldErrors<TFormData>;
}

function CreditCardInput<TFormData extends CreditCardFormFields = CreditCardFormFields>({
    onCardDetailsChange,
    initialCardDetails = {},
    isReadOnly = false,
    hideBillingAddress = false,
    register,
    errors,
}: CreditCardInputProps<TFormData>) {
    const [cardDetails, setCardDetails] = useState<business_integrations.ICardDetails>({
        number: "",
        expiryMonth: "",
        expiryYear: "",
        cvv: "",
        cardholderName: "",
        billingAddress: "",
        billingAddressStreet: "",
        billingAddressPostalCode: "",
        billingAddressCity: "",
        billingAddressState: "",
        billingAddressCountry: "",
        ...initialCardDetails,
    });
    const [localErrors, setLocalErrors] = useState({
        expiry: "",
        cvv: "",
        cardholderName: "",
    });
    const [cardNumberError, setCardNumberError] = useState("");
    const [expiryInput, setExpiryInput] = useState(
        initialCardDetails.expiryMonth && initialCardDetails.expiryYear
            ? initialCardDetails.expiryMonth + "/" + initialCardDetails.expiryYear
            : "",
    );
    const [address, setAddress] = useState<string | undefined>(initialCardDetails.billingAddress);
    const [addressLoading, setAddressLoading] = useState(false);

    // Helper function to safely extract error messages
    const getErrorMessage = (fieldName: Path<TFormData>): string => {
        const error = errors?.[fieldName];
        if (error && typeof error === "object" && "message" in error) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            return String(error.message) || "";
        }
        return "";
    };

    // Combine local errors with form errors
    const getFieldError = (fieldName: Path<TFormData>, localError?: string): string => {
        const formError = getErrorMessage(fieldName);
        return formError || localError || "";
    };

    const validateCardNumber = (value: string) => {
        if (value && !valid.number(value).isValid) {
            setCardNumberError("Invalid card number");
        } else {
            setCardNumberError("");
        }
    };

    const debouncedCardValidation = debounce((value: string) => validateCardNumber(value), 2000);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let formattedValue = value;
        if (name === "number") {
            formattedValue = value
                .replace(/\D/g, "")
                .replace(/(.{4})/g, "$1 ")
                .trim();
            setCardNumberError(""); // Clear error when user starts typing
            debouncedCardValidation(formattedValue);
        }
        const updatedDetails = { ...cardDetails, [name]: formattedValue };
        setCardDetails(updatedDetails);
        onCardDetailsChange(updatedDetails);
    };

    const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const prevValue = expiryInput;
        // Allow deletion of the slash
        if (prevValue.length > input.length && prevValue.includes("/")) {
            const newValue = input.replace("/", "");
            setExpiryInput(newValue);
            updateCardDetails(newValue.slice(0, 2), newValue.slice(2));
            return;
        }
        const sanitizedInput = input.replace(/[^\d/]/g, "");
        let formattedValue = "";
        const parts = sanitizedInput.split("/");
        const monthPart = parts[0].slice(0, 2);
        const yearPart = parts[1]?.slice(0, 2) || "";
        if (monthPart) {
            const month = parseInt(monthPart, 10);
            if (month > 0 && month <= 12) {
                formattedValue = monthPart;
                if (monthPart.length === 2 && (yearPart || input.includes("/"))) {
                    formattedValue += "/";
                }
            } else if (monthPart.length === 1 && month <= 1) {
                formattedValue = monthPart;
            }
        }
        if (yearPart) {
            formattedValue += yearPart;
        }
        setExpiryInput(formattedValue);
        updateCardDetails(monthPart, yearPart);
    };

    const updateCardDetails = (month: string, year: string) => {
        const updatedDetails = {
            ...cardDetails,
            expiryMonth: month || "",
            expiryYear: year || "",
        };
        setCardDetails(updatedDetails);
        onCardDetailsChange(updatedDetails);
        validateExpiryDate(month, year);
    };

    const validateExpiryDate = (month: string, year: string) => {
        if (!month && !year) {
            setLocalErrors((prev) => ({ ...prev, expiry: "Expiration date is required" }));
            return;
        }
        if (month.length === 2 && !year) {
            return; // Don't validate yet, user is still typing
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;
        const expMonth = parseInt(month, 10);
        const expYear = parseInt(year, 10);
        if (isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
            setLocalErrors((prev) => ({ ...prev, expiry: "Invalid expiration month" }));
        } else if (
            year.length === 2 &&
            (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth))
        ) {
            setLocalErrors((prev) => ({ ...prev, expiry: "Card has expired" }));
        } else {
            setLocalErrors((prev) => ({ ...prev, expiry: "" }));
        }
    };

    const parseAddress = useMemo(
        // TODO: PAYMENTS definitely not the best way to parse an address
        () => (address_: string) => {
            const parts = address_.split(",").map((part) => part.trim());
            const zipMatch = parts[parts.length - 1].match(/\d{5}(-\d{4})?/);
            const zip = zipMatch ? zipMatch[0] : "";
            let street = "";
            if (parts.length >= 3) {
                street = parts.slice(0, -3).join(", ");
            } else if (parts.length === 2) {
                street = parts[0];
            }
            return { street, zip };
        },
        [],
    );

    const handleAddressChange = useCallback(
        (newAddress: string | undefined) => {
            setAddress(newAddress);
            if (newAddress) {
                const { street, zip } = parseAddress(newAddress);
                const updatedDetails = {
                    ...cardDetails,
                    billingAddressStreet: street,
                    billingAddressPostalCode: zip,
                    billingAddress: newAddress,
                };
                setCardDetails(updatedDetails);
                onCardDetailsChange(updatedDetails);
            }
        },
        [onCardDetailsChange, parseAddress, cardDetails],
    );

    // Get combined expiry error (prioritizing form errors)
    const expiryError =
        getErrorMessage("expiryMonth" as Path<TFormData>) ||
        getErrorMessage("expiryYear" as Path<TFormData>) ||
        localErrors.expiry;

    return (
        <>
            <Form.Group>
                <Form.Label>Cardholder Name</Form.Label>
                <Form.Control
                    {...(register ? register("cardholderName" as Path<TFormData>) : {})}
                    type="text"
                    name="cardholderName"
                    value={cardDetails.cardholderName}
                    onChange={handleInputChange}
                    isInvalid={
                        !!(
                            getErrorMessage("cardholderName" as Path<TFormData>) ||
                            localErrors.cardholderName
                        )
                    }
                    placeholder="Full Name"
                    required
                    autoComplete="cc-name"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                />
                <Form.Control.Feedback type="invalid">
                    {getFieldError("cardholderName" as Path<TFormData>, localErrors.cardholderName)}
                </Form.Control.Feedback>
            </Form.Group>
            <div className="mt-2" />
            <Form.Group>
                <Form.Label>Card Number</Form.Label>
                <Form.Control
                    {...(register ? register("cardNumber" as Path<TFormData>) : {})}
                    type="text"
                    name="number"
                    value={cardDetails.number}
                    onChange={handleInputChange}
                    isInvalid={
                        !!(getErrorMessage("cardNumber" as Path<TFormData>) || cardNumberError)
                    }
                    placeholder="1234 1234 1234 1234"
                    maxLength={19}
                    autoComplete="cc-number"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                />
                <Form.Control.Feedback type="invalid">
                    {getFieldError("cardNumber" as Path<TFormData>, cardNumberError)}
                </Form.Control.Feedback>
            </Form.Group>
            <div className="mt-2" />
            <div className="row">
                <div className="col-md-6">
                    <Form.Group className="">
                        <Form.Label>Expiration Date</Form.Label>
                        <Form.Control
                            type="text"
                            name="expiry"
                            value={expiryInput}
                            onChange={handleExpiryChange}
                            placeholder="MM/YY"
                            required
                            maxLength={5}
                            pattern="\d{1,2}/?\d{0,2}"
                            autoComplete="cc-exp"
                            isInvalid={!!expiryError}
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                        />
                        {expiryError && (
                            <Form.Control.Feedback type="invalid">
                                {expiryError}
                            </Form.Control.Feedback>
                        )}
                        {/* Hidden fields for form validation */}
                        {register && (
                            <>
                                <Form.Control
                                    {...register("expiryMonth" as Path<TFormData>)}
                                    type="hidden"
                                />
                                <Form.Control
                                    {...register("expiryYear" as Path<TFormData>)}
                                    type="hidden"
                                />
                            </>
                        )}
                    </Form.Group>
                </div>
                <div className="col-md-6">
                    <Form.Group className="">
                        <Form.Label>Security Code</Form.Label>
                        <Form.Control
                            {...(register ? register("cvv" as Path<TFormData>) : {})}
                            type="text"
                            name="cvv"
                            value={cardDetails.cvv}
                            onChange={handleInputChange}
                            placeholder="CVV"
                            required
                            maxLength={4}
                            pattern="\d*"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            isInvalid={
                                !!(getErrorMessage("cvv" as Path<TFormData>) || localErrors.cvv)
                            }
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                        />
                        <Form.Control.Feedback type="invalid">
                            {getFieldError("cvv" as Path<TFormData>, localErrors.cvv)}
                        </Form.Control.Feedback>
                    </Form.Group>
                </div>
            </div>
            <div className="mt-2" />
            {!hideBillingAddress && (
                <Form.Group>
                    <Form.Label>Billing Address</Form.Label>
                    {addressLoading && <LobbieSpinner small />}
                    <SuspendedAddressAutocomplete
                        name="billingAddress"
                        address={address}
                        setAddress={null}
                        setAddressString={handleAddressChange}
                        setLoading={setAddressLoading}
                        required={true}
                        isReadOnly={isReadOnly}
                    />
                    {register && (
                        <Form.Control
                            {...register("billingAddress" as Path<TFormData>)}
                            type="hidden"
                        />
                    )}
                    {getErrorMessage("billingAddress" as Path<TFormData>) && (
                        <div className="text-danger mt-1">
                            <small>{getErrorMessage("billingAddress" as Path<TFormData>)}</small>
                        </div>
                    )}
                </Form.Group>
            )}
        </>
    );
}

export default CreditCardInput;
